import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface FormInstanceRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  form_definition_id: string;
  patient_id: string;
  encounter_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  visit_id: string | null;
  status: string; // 'draft' | 'completed'
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  completed_at: string | null;
}

export interface FormInstanceValueRow {
  id: string;
  form_instance_id: string;
  field_key: string;
  value: string | null;
  created_at: string;
  updated_at: string;
}

export interface FormInstanceSignatureRow {
  id: string;
  form_instance_id: string;
  field_key: string;
  stroke_data: string; // JSON text on client
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

// ─── List row with joined form name ───

export interface FormInstanceListRow extends FormInstanceRow {
  form_name: string | null;
  default_view: string | null; // 'render' | 'form'
}

// ─── Queries ───

/**
 * List form instances for an encounter, joined with form_definitions for the name.
 */
export function qEncounterFormInstances(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['form-instances', 'by-encounter', encounterId] as const,
    sql: `SELECT fi.*, fd.name AS form_name, fd.default_view
          FROM form_instances fi
          LEFT JOIN form_definitions fd ON fd.id = fi.form_definition_id
          WHERE fi.encounter_id = ?
          ORDER BY fi.created_at DESC`,
    params: [encounterId],
    watch: ['form_instances', 'form_definitions'],
  };
}

/**
 * List form instances for a patient (all encounters).
 */
export function qPatientFormInstances(patientId: string): QuerySpec<[string]> {
  return {
    key: ['form-instances', 'by-patient', patientId] as const,
    sql: `SELECT fi.*, fd.name AS form_name, fd.default_view
          FROM form_instances fi
          LEFT JOIN form_definitions fd ON fd.id = fi.form_definition_id
          WHERE fi.patient_id = ?
          ORDER BY fi.created_at DESC`,
    params: [patientId],
    watch: ['form_instances', 'form_definitions'],
  };
}

/**
 * Fetch a single form instance by ID.
 */
export function qFormInstanceById(id: string): QuerySpec<[string]> {
  return {
    key: ['form-instances', 'instance', id] as const,
    sql: `SELECT fi.*, fd.name AS form_name, fd.default_view
          FROM form_instances fi
          LEFT JOIN form_definitions fd ON fd.id = fi.form_definition_id
          WHERE fi.id = ?`,
    params: [id],
    watch: ['form_instances', 'form_definitions'],
  };
}

/**
 * Fetch all values for a form instance.
 */
export function qFormInstanceValues(instanceId: string): QuerySpec<[string]> {
  return {
    key: ['form-instances', 'values', instanceId] as const,
    sql: `SELECT * FROM form_instance_values WHERE form_instance_id = ? ORDER BY field_key ASC`,
    params: [instanceId],
    watch: ['form_instance_values'],
  };
}

/**
 * Fetch all signatures for a form instance.
 */
export function qFormInstanceSignatures(instanceId: string): QuerySpec<[string]> {
  return {
    key: ['form-instances', 'signatures', instanceId] as const,
    sql: `SELECT * FROM form_instance_signatures WHERE form_instance_id = ? ORDER BY field_key ASC`,
    params: [instanceId],
    watch: ['form_instance_signatures'],
  };
}
